import type { CSSProperties } from "react";

/**
 * 1NOW-11113: the soft dark patch behind the hero text.
 *
 * A radial gradient puts the weight on the side the text sits on (`--scrim-x`, a
 * percentage of the patch's width), and a mask then fades the patch to nothing on all
 * four sides, so it never ends in a visible edge. `--scrim-fx` and `--scrim-fy` set how
 * far the fade runs in from the left/right and top/bottom; the caller sizes the patch
 * so the text sits just inside that fade.
 */

const EASE = [0, 0.16, 0.5, 0.84, 1]; // smoothstep, so the fade has no visible start or end line
const fade = (dir: string, len: string) => {
  const l = `var(${len})`;
  const rise = EASE.map((a, i) => `rgba(0,0,0,${a}) calc(${l} * ${i / 4})`);
  const fall = [...EASE]
    .reverse()
    .map((a, i) => `rgba(0,0,0,${a}) calc(100% - ${l} * ${1 - i / 4})`);
  return `linear-gradient(${dir}, ${[...rise, ...fall].join(", ")})`;
};
const mask = `${fade("to right", "--scrim-fx")}, ${fade("to bottom", "--scrim-fy")}`;

const patch = (rgb: string, peak: number, mid: number): CSSProperties => ({
  background: `radial-gradient(ellipse 100% 100% at var(--scrim-x, 50%) 50%, rgba(${rgb},${peak}) 0%, rgba(${rgb},${mid}) 55%, rgba(${rgb},0) 100%)`,
  WebkitMaskImage: mask,
  maskImage: mask,
  WebkitMaskComposite: "source-in",
  maskComposite: "intersect",
});

/** For light heading text: a dark patch. */
export const heroScrimStyle: CSSProperties = patch("0,0,0", 0.45, 0.25);

/** Keeps a tenant-picked heading colour readable on a busy photo. */
export const heroTextShadow: CSSProperties = { textShadow: "0 2px 12px rgba(0,0,0,0.35)" };

/**
 * 1NOW-11113 (reopened): the dark patch and shadow only help LIGHT text. A dark
 * heading (#0C1E01, a dark green) on a dark photo got a black patch and a black
 * shadow behind it and stayed unreadable. For a dark heading the patch and the
 * shadow are white instead, so it sits on a lightened area of the photo.
 */
export const heroLightScrimStyle: CSSProperties = patch("255,255,255", 0.7, 0.5);
export const heroLightTextShadow: CSSProperties = { textShadow: "0 2px 12px rgba(255,255,255,0.55)" };

const channel = (v: number) => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

/**
 * The heading is "dark" when white would contrast with it more than black
 * does, i.e. when a dark patch behind it would make things worse. A colour
 * that is not a hex (or is missing) is treated as light, which is what the
 * provider defaults the heading to (#ffffff) and what shipped before.
 */
export const isDarkHeading = (color?: string | null): boolean => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(color ?? "").trim());
  if (!m) return false;
  const h = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1];
  const n = parseInt(h, 16);
  const lum = 0.2126 * channel((n >> 16) & 255) + 0.7152 * channel((n >> 8) & 255) + 0.0722 * channel(n & 255);
  // contrast with white (1.05 / (lum + .05)) beats contrast with black ((lum + .05) / .05)
  return 1.05 / (lum + 0.05) > (lum + 0.05) / 0.05;
};

/** The patch and the heading shadow to use for a heading of this colour. */
export const heroScrimFor = (headingColor?: string | null): { scrim: CSSProperties; shadow: CSSProperties } =>
  isDarkHeading(headingColor)
    ? { scrim: heroLightScrimStyle, shadow: heroLightTextShadow }
    : { scrim: heroScrimStyle, shadow: heroTextShadow };

/**
 * 1NOW-11113 (reopened again): the description sits on the same patch as the heading, so it
 * follows the same rule. A dark heading gets a light patch, and a pale description (#DBEAFE)
 * on it measured about 2.2:1. When the description's tone is the wrong one for the patch it
 * takes the heading's colour, which is the right tone by construction; a description that
 * already suits the patch is left exactly as the tenant picked it. The colour travels as the
 * CSS variable the `banner-description-color` utility reads, so a class that is `!important`
 * (Design B's) follows it too. A missing or non-hex colour counts as light, like the provider's
 * #ffffff default.
 */
const rgbOf = (color?: string | null): string => {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(color ?? "").trim());
  if (!m) return "255, 255, 255";
  const h = m[1].length === 3 ? m[1].split("").map((c) => c + c).join("") : m[1];
  const n = parseInt(h, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

export const heroDescriptionStyle = (
  headingColor?: string | null,
  descriptionColor?: string | null
): CSSProperties | undefined =>
  isDarkHeading(headingColor) === isDarkHeading(descriptionColor)
    ? undefined
    : ({ "--c-banner-description-color": rgbOf(headingColor) } as CSSProperties);
